import {useCallback} from 'react';
import root from 'window-or-global';
import epicTracking from 'epic-tracking';
import parse from 'url-parse';
import {extractSlugLocale} from '@epic-mw/localization';
import {ClickEventHandler, ClickAnchorEventHandler} from '../types/element.types';
import {getLocale} from '@epic-core/common';

export interface AnalyticEventProps {
    accountId?: string;
    eventCategory?: string;
    eventAction: string;
    eventLabel?: string | boolean | number;
    eventValue?: string | boolean | number;
    interactionType?: string;
    overrides?: {[key: string]: string | boolean | number};
}

const queue: AnalyticEventProps[] = [];

const send = (event) => {
    if (epicTracking && typeof epicTracking.trackEvent === 'function') {
        epicTracking.trackEvent('Interaction', event);
        if (process.env.NODE_ENV === 'development') {
            console.log('[epic-tracking] emitted event', event.eventAction, event);
        }
    }
};

/**
 * _epicTracking will exist when the tracking script has loaded asynchronously
 * Queue events up before then
 */
const handleQueue = () => {
    if (!queue.length) return;

    setTimeout(() => {
        if (root._epicTracking) {
            const event = queue.shift();
            if (event) {
                send(event);
            }
        }
        handleQueue();
    }, 2500);
};

const defaultEventCategory = 'UE_COM';
export const setDefaultEventCategory = (newEventCategory: string): void => {
    if (newEventCategory) {
        root._analyticEventCategory = newEventCategory;
    }
};

export function emitEvent(props: AnalyticEventProps | undefined): void {
    const analytics: AnalyticEventProps = props || ({} as any);
    const {eventAction, eventLabel, eventValue, interactionType, overrides} = analytics;

    if (!eventAction) return;

    const preload = root.unrealEngine_dataPreload || {};
    const accountStore = preload.AccountStore || {};
    const id = accountStore.id;
    const clientConfig = root.clientEnvConfig || {};

    const appEnv = process?.env?.EPIC_ENV || '';

    let event = {
        eventType: 'EPIC_WEB_ANALYTICS_EVENT_INTERACTION',
        application: 'EPICWEBTRACKING',
        appEnv: appEnv || clientConfig.EPIC_ENV,
        useAgent: root.navigator.userAgent,
        eventDate: new Date().toISOString(),
        account_id: id || '',
        pageUrl: root.location.href,
        interactionType: interactionType || 'click',
        eventCategory: root._analyticEventCategory || defaultEventCategory,
        eventAction,
        eventLabel,
        eventValue
    };

    if (overrides && Object.keys(overrides).length) {
        event = Object.assign(event, overrides);
    }

    if (!root._epicTracking) {
        queue.push(event);
        handleQueue();
    } else {
        send(event);
    }
}

export const useAnalyticsCallback = (
    onClick: ClickEventHandler | ClickAnchorEventHandler | undefined,
    id?: string,
    href?: string,
    eventAction = 'link.click'
): ((e: any) => void) => {
    return useCallback(
        (e) => {
            if (onClick && typeof onClick === 'function') {
                onClick(e);
            }
            const parsedCurrentUrl = parse(root.location.href);
            const {pageSlug} = extractSlugLocale(parsedCurrentUrl.pathname, getLocale());
            emitEvent({eventAction, eventLabel: id || `${pageSlug}${href ? ` | ${href}` : ''}`});
        },
        [onClick, id, href]
    );
};
